import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Detail, Checkbox, Tooltip, Optional } from 'components/common';
import { TreeSelect } from 'components/common';
import WorkItemTree from './WorkItemTree';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { AUTO_AI_ACTIVITIES } from '../utils/scileadsAiActivities';
import { colours } from 'utils/theme';

// Add styled components
const ActivitySelectorContainer = styled.div`
  width: 100%;
`;

const ActivityCount = styled.div`
  text-align: right;
  color: #8d8d8d;
  font-size: 13px;
  margin-top: 3px;
`;

// Utility to recursively disable actual activities when auto ai activity is 2
function disableActivities(nodes, ai2Selected) {
  return nodes.map(node => {
    const isAi2 = node.value === AUTO_AI_ACTIVITIES.AI_2.value;
    if (ai2Selected) {
      if (isAi2) {
        return {
          ...node,
          disabled: false,
          children: node.children
            ? disableActivities(node.children, ai2Selected)
            : undefined,
        };
      } else {
        return {
          ...node,
          disabled: true,
          children: node.children
            ? disableActivities(node.children, ai2Selected)
            : undefined,
        };
      }
    } else {
      return {
        ...node,
        disabled: false,
        children: node.children
          ? disableActivities(node.children, ai2Selected)
          : undefined,
      };
    }
  });
}

function ActivitySelector({
  allItems,
  recentItems,
  searchResults,
  searchLoading,
  hasActivities,
  allItemsLoading,
  recentItemsLoading,
  workItemId,
  excludeOldWorkItems,
  onExcludeOldWorkItemsChange,
  loading,
  disabled,
  onSelectionChange,
  clearSelectionTrigger,
  handleActivitySearch,
  initialSelectedValues,
  autoAiActivities,
  maxCount = 2,
}) {
  // Selection state
  const [selectedWorkItems, setSelectedWorkItems] = useState([]);
  const [selectedWorkItemLabels, setSelectedWorkItemLabels] = useState({});
  const [initialWorkItem, setInitialWorkItem] = useState([]);
  const [initialWorkItemProcessed, setInitialWorkItemProcessed] = useState(
    false
  );
  const hasHandledInitialSelection = useRef(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Create debounced version of handleActivitySearch
  const debouncedActivitySearch = debounce(term => {
    if (handleActivitySearch) {
      handleActivitySearch(term);
    }
  }, 500);

  // Reset search when dropdown loses focus
  const resetSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSearchTerm('');
    setIsSearching(false);
    // Also clear the parent's search if we're resetting
    if (handleActivitySearch) {
      handleActivitySearch('');
    }
  }, [handleActivitySearch]);

  // Effect to update searching state based on search term
  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
  }, [searchTerm]);

  // Handle selection change
  const handleSelectedWorkItemsChange = useCallback(
    (value, node) => {
      // If maxCount is 1, only keep the last selected value
      if (maxCount === 1 && value.length > 1) {
        value = [value[value.length - 1]];
        node = [node[node.length - 1]];
      }
      // If AI_2 is selected, only keep AI_2
      if (value.includes(AUTO_AI_ACTIVITIES.AI_2.value)) {
        value = value.filter(v => v === AUTO_AI_ACTIVITIES.AI_2.value);
        node = node.filter(n => n.value === AUTO_AI_ACTIVITIES.AI_2.value);
      }
      setSelectedWorkItems(value);
      setSelectedWorkItemLabels(prev => {
        const updatedLabels = {};
        value.forEach((val, index) => {
          const category = node[index].category;
          if (!updatedLabels[category]) {
            updatedLabels[category] = {};
          }
          updatedLabels[category][val] = node[index];
        });
        Object.keys(prev).forEach(category => {
          if (!updatedLabels[category]) {
            delete updatedLabels[category];
          } else {
            Object.keys(prev[category]).forEach(key => {
              if (!value.includes(key)) {
                delete updatedLabels[category][key];
              }
            });
          }
        });
        return updatedLabels;
      });
      if (initialWorkItem.length) setInitialWorkItem([]);
    },
    [initialWorkItem.length, maxCount]
  );

  // Expose selection to parent
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedWorkItems, selectedWorkItemLabels);
    }
  }, [selectedWorkItems, selectedWorkItemLabels, onSelectionChange]);

  // Tree data logic
  const getDisplayWorkItems = useCallback(() => {
    if (isSearching && searchResults) {
      return searchResults;
    }
    return excludeOldWorkItems ? recentItems : allItems;
  }, [isSearching, searchResults, excludeOldWorkItems, recentItems, allItems]);

  const isAi2Selected = selectedWorkItems.includes(
    AUTO_AI_ACTIVITIES.AI_2.value
  );

  const processedTreeData = useMemo(() => {
    if (allItemsLoading) return [];
    const allItemsArray = allItems?.groupedResults || [];
    const showBothAiOptions = allItemsArray.length >= 2;
    const showOnlyAi1 = allItemsArray.length === 1;
    let filteredAutoActivities;
    if (showBothAiOptions) {
      filteredAutoActivities = autoAiActivities;
    } else if (showOnlyAi1) {
      filteredAutoActivities = autoAiActivities.filter(
        act => act.value === AUTO_AI_ACTIVITIES.AI_1.value
      );
    } else {
      filteredAutoActivities = [];
    }
    const currentDisplayWorkItems = getDisplayWorkItems();
    if (!currentDisplayWorkItems)
      return disableActivities([...filteredAutoActivities], isAi2Selected);

    // Build the tree data for actual activities
    const treeData = WorkItemTree.buildWorkItemTreeData(
      currentDisplayWorkItems,
      selectedWorkItemLabels,
      workItemId ? setInitialWorkItem : () => {},
      workItemId ? workItemId : null
    );

    // Only handle initialSelectedValues if workItemId is not being processed
    if (
      !workItemId &&
      !hasHandledInitialSelection.current &&
      (initialSelectedValues === 1 || initialSelectedValues === 2)
    ) {
      const staticActivity = filteredAutoActivities.find(
        activity => activity.value === `scileads-ai-${initialSelectedValues}`
      );
      if (staticActivity) {
        hasHandledInitialSelection.current = true;
        setSelectedWorkItems([staticActivity.value]);
        setSelectedWorkItemLabels({
          static: { [staticActivity.value]: staticActivity },
        });
      }
    }

    return disableActivities(
      [...filteredAutoActivities, ...treeData],
      isAi2Selected
    );
  }, [
    getDisplayWorkItems,
    selectedWorkItemLabels,
    workItemId,
    isAi2Selected,
    autoAiActivities,
    initialSelectedValues,
    allItems,
    allItemsLoading,
  ]);

  // Process initial work item selection if provided
  useEffect(() => {
    if (initialWorkItem.length && !initialWorkItemProcessed) {
      setInitialWorkItemProcessed(true);
      handleSelectedWorkItemsChange(initialWorkItem[0], initialWorkItem[1]);
    }
  }, [
    initialWorkItem,
    initialWorkItemProcessed,
    handleSelectedWorkItemsChange,
  ]);

  // Reset the processed flag when workItemId changes
  useEffect(() => {
    // Only reset if initialSelectedValues is not set AND there's no workItemId
    if (!initialSelectedValues && !workItemId) {
      setInitialWorkItemProcessed(false);
    }
  }, [workItemId, initialSelectedValues]);

  // Loading state for the selector
  const isSelectorLoading = loading || allItemsLoading || recentItemsLoading;

  // Effect to clear selection when clearSelectionTrigger changes
  useEffect(() => {
    setSelectedWorkItems([]);
    setSelectedWorkItemLabels({});
    setInitialWorkItem([]);
    // Don't reset initialWorkItemProcessed if there's a workItemId, as it will cause re-selection
    if (!workItemId) {
      setInitialWorkItemProcessed(false);
    }
    setSearchTerm('');
    setIsSearching(false);
  }, [clearSelectionTrigger, workItemId]);

  // Handle search input change and update parent's search term
  const handleSearchChange = useCallback(
    term => {
      setSearchTerm(term);
      debouncedActivitySearch(term);
    },
    [debouncedActivitySearch]
  );

  // Add after selection state declarations
  useEffect(() => {
    // Only clear if a auto activity is selected and is not present in selected activities
    if (
      selectedWorkItems.length > 0 &&
      selectedWorkItems.some(
        val =>
          val.startsWith('scileads-ai-') &&
          !autoAiActivities.some(act => act.value === val)
      )
    ) {
      setSelectedWorkItems([]);
      setSelectedWorkItemLabels({});
    }
  }, [autoAiActivities, selectedWorkItems]);

  useEffect(() => {
    if (maxCount === 1 && selectedWorkItems.length > 1) {
      setSelectedWorkItems([]);
      setSelectedWorkItemLabels({});
    }
  }, [maxCount, selectedWorkItems]);

  return (
    <>
      <Detail
        label={
          <>
            Activities
            <Optional />
          </>
        }
      >
        <Tooltip
          title={
            !hasActivities
              ? 'Option disabled because the contact has no activities.'
              : ''
          }
        >
          <ActivitySelectorContainer>
            <TreeSelect
              getPopupContainer={triggerNode => triggerNode.parentNode}
              selectedValues={selectedWorkItems}
              size='middle'
              filterTreeNode={false}
              treeCheckable
              showArrow
              maxCount={maxCount}
              placeholder={'Type to search activities (e.g. carcinoma)'}
              treeData={processedTreeData}
              onChange={handleSelectedWorkItemsChange}
              style={{ width: '100%' }}
              onSearch={handleSearchChange}
              onDropdownBlur={resetSearch}
              loading={searchLoading || isSelectorLoading}
              autoClearSearchValue={true}
              treeExpandAction={'click'}
              disabled={isSelectorLoading || disabled || !hasActivities}
              titleIconStyle={{
                fontSize: 14,
                width: 16,
                height: 16,
                marginRight: 6,
                verticalAlign: 'middle',
                display: 'inline-block',
                color: colours.grey,
                position: 'relative',
              }}
              titleTextStyle={{
                position: 'relative',
              }}
            />
            <ActivityCount>
              {maxCount === 1
                ? selectedWorkItems.length
                : selectedWorkItems.includes(AUTO_AI_ACTIVITIES.AI_2.value)
                ? '2'
                : selectedWorkItems.length}{' '}
              / {maxCount}
            </ActivityCount>
          </ActivitySelectorContainer>
        </Tooltip>
      </Detail>
      <Detail style={{ marginBottom: '16px' }}>
        <Tooltip
          title={
            !hasActivities
              ? 'Option disabled because the contact has no activities.'
              : 'When enabled, activities older than one year will be deprioritised in favour of more recent and future activities. For example, if an activity within the past year has 5 keyword matches and an activity from 2 years ago has 30 matches, the more recent activity would be prioritised.'
          }
        >
          <Checkbox
            checked={excludeOldWorkItems}
            disabled={isSelectorLoading || disabled || !hasActivities}
            onChange={onExcludeOldWorkItemsChange}
            labelText='Deprioritise activities older than one year'
          ></Checkbox>
        </Tooltip>
      </Detail>
    </>
  );
}

ActivitySelector.propTypes = {
  allItems: PropTypes.object,
  recentItems: PropTypes.object,
  searchResults: PropTypes.object,
  searchLoading: PropTypes.bool,
  hasActivities: PropTypes.bool,
  allItemsLoading: PropTypes.bool,
  recentItemsLoading: PropTypes.bool,
  workItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  excludeOldWorkItems: PropTypes.bool,
  onExcludeOldWorkItemsChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  clearSelectionTrigger: PropTypes.number,
  handleActivitySearch: PropTypes.func,
  initialSelectedValues: PropTypes.array,
  autoAiActivities: PropTypes.array.isRequired,
  maxCount: PropTypes.number,
};

ActivitySelector.defaultProps = {
  excludeOldWorkItems: true,
  hasActivities: false,
  searchLoading: false,
  loading: false,
  disabled: false,
  allItemsLoading: false,
  recentItemsLoading: false,
};

export default ActivitySelector;
